import React, { createContext, useContext, useState, useEffect } from 'react';
import { SEC_AUTH_M_TK } from 'constants/AuthConstant';
import { getPermissionInfo, decodeToken } from 'utils/jwtUtils';
import { fetchUsageLimitsAndCounts, updateLimitsWithApiData } from 'services/UsageTrackingService';
import { safeGetToken } from 'utils/safeStorage';
import {
  PREMIUM_SUBSCRIPTION_TYPES 
} from 'constants/PremiumConstant';
// Custom event name for subscription updates
export const SUBSCRIPTION_UPDATE_EVENT = 'subscription-update';

// Create context
const SubscriptionContext = createContext({
  subscriptionType: null,
  userId: null,
  isPremium: false,
  isPlanActive: false,
  hasAccess: false,
  permissionInfo: null,
  isLoading: true,
  subscriptionFeatures: [], // <-- add this
});

/**
 * Provider component that wraps your app and makes subscription info available to any
 * child component that calls useSubscription().
 */
export const SubscriptionProvider = ({ children }) => {
  const [subscriptionState, setSubscriptionState] = useState({
    subscriptionType: null,
    userId: null,
    isPremium: false,
    isPlanActive: false,
    hasAccess: false,
    permissionInfo: null,
    isLoading: true,
    subscriptionFeatures: [], // <-- add this
  });

  /**
   * Helper function to extract subscription features from token (SonarQube fix - reduce cognitive complexity)
   */
  const extractSubscriptionFeatures = (token) => {
    const decoded = decodeToken(token);
    if (decoded?.enabled_features && Array.isArray(decoded.enabled_features.subscription_features)) {
      return decoded.enabled_features.subscription_features;
    }
    return [];
  };

  /**
   * Helper function to set default subscription state (SonarQube fix - reduce cognitive complexity)
   */
  const setDefaultSubscriptionState = () => {
    setSubscriptionState({
      subscriptionType: null,
      userId: null,
      isPremium: false,
      isPlanActive: false,
      hasAccess: false,
      permissionInfo: null,
      isLoading: false,
      subscriptionFeatures: [],
    });
  };

  /**
   * Helper function to fetch and update usage data (SonarQube fix - reduce cognitive complexity)
   */
  const fetchAndUpdateUsageData = async (subscriptionType) => {
    if (!subscriptionType) {
      return;
    }

    try {
      console.log('🔄 Fetching usage limits and counts...');
      const usageData = await fetchUsageLimitsAndCounts();

      if (usageData) {
        console.log('✅ Usage data received, updating limits...');
        updateLimitsWithApiData(usageData, subscriptionType);
      } else {
        console.log('⚠️ No usage data received, using static limits');
      }
    } catch (error) {
      console.error('❌ Error fetching usage data:', error);
      // Continue with static limits if API fails
    }
  };

  /**
   * Helper function to process valid token (SonarQube fix - reduce cognitive complexity)
   */
  const processValidToken = async (token) => {
    const permissionInfo = getPermissionInfo(token);
    console.log('Permission info:', permissionInfo);

    // Determine if user is premium based on subscription type
    const isPremium = PREMIUM_SUBSCRIPTION_TYPES.includes(permissionInfo.subscriptionType);
    
    // Extract subscription_features from the token
    const subscriptionFeatures = extractSubscriptionFeatures(token);

    setSubscriptionState({
      subscriptionType: permissionInfo.subscriptionType,
      userId: permissionInfo.tenantId, // Using tenantId as userId for consistency
      isPremium: isPremium,
      isPlanActive: permissionInfo.isPlanActive,
      hasAccess: permissionInfo.hasAccess,
      permissionInfo: permissionInfo,
      isLoading: false,
      subscriptionFeatures,
    });

    // Fetch usage data after setting subscription info
    await fetchAndUpdateUsageData(permissionInfo.subscriptionType);
  };

  // Function to check subscription status from token and fetch usage data
  const checkSubscription = async () => {
    console.log('Checking subscription status...');
    const token = safeGetToken(SEC_AUTH_M_TK);

    if (token && typeof token === 'string') {
      try {
        await processValidToken(token);
      } catch (error) {
        console.error('Error processing token in SubscriptionContext:', error);
        setDefaultSubscriptionState();
      }
    } else {
      console.log('No valid token found, setting to default state');
      setDefaultSubscriptionState();
    }
  };

  useEffect(() => {
    // Check subscription on mount
    checkSubscription();

    // Set up event listener for storage changes (in case token changes in another tab)
    const handleStorageChange = (e) => {
      if (e.key === SEC_AUTH_M_TK) {
        console.log('Auth token changed in storage, updating subscription');
        checkSubscription();
      }
    };

    // Set up event listener for custom subscription update event
    const handleSubscriptionUpdate = () => {
      console.log('Subscription update event received');
      checkSubscription();
    };

    // Set up periodic check for token changes (fallback for same-tab updates)
    let currentToken = localStorage.getItem(SEC_AUTH_M_TK);
    const tokenCheckInterval = setInterval(() => {
      const newToken = localStorage.getItem(SEC_AUTH_M_TK);
      if (newToken !== currentToken) {
        console.log('Token changed detected via polling, updating subscription');
        currentToken = newToken;
        checkSubscription();
      }
    }, 1000); // Check every second

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener(SUBSCRIPTION_UPDATE_EVENT, handleSubscriptionUpdate);

    // Clean up
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener(SUBSCRIPTION_UPDATE_EVENT, handleSubscriptionUpdate);
      clearInterval(tokenCheckInterval);
    };
  }, []);

  return (
    <SubscriptionContext.Provider value={subscriptionState}>
      {children}
    </SubscriptionContext.Provider>
  );
};

// Custom hook to use the subscription context
export const useSubscription = () => useContext(SubscriptionContext);

/**
 * Trigger a manual subscription update check
 * Call this after subscription changes (e.g., after payment completion)
 */
export const triggerSubscriptionUpdate = () => {
  console.log('Triggering subscription update...');
  window.dispatchEvent(new CustomEvent(SUBSCRIPTION_UPDATE_EVENT));
};

export default SubscriptionContext;
